/**
 * Shared IANA timezone options used by personal Profile settings (users.timezone)
 * and guide Availability scheduling. Kept as a curated short-list of common
 * North/South American, European, Asian, and Oceanian zones rather than the full
 * Intl database to keep the picker scannable.
 */

export interface TimezoneOption {
  value: string;
  label: string;
}

export const TIMEZONE_LIST: TimezoneOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET) — New York, Toronto' },
  { value: 'America/Chicago', label: 'Central Time (CT) — Chicago, Dallas' },
  { value: 'America/Denver', label: 'Mountain Time (MT) — Denver, Calgary' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) — Los Angeles, Vancouver' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT) — Anchorage' },
  { value: 'America/Whitehorse', label: 'Yukon Time (YT) — Whitehorse' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT) — Honolulu' },
  { value: 'America/Toronto', label: 'Eastern Time (ET) — Toronto' },
  { value: 'America/Vancouver', label: 'Pacific Time (PT) — Vancouver' },
  { value: 'America/Edmonton', label: 'Mountain Time (MT) — Edmonton' },
  { value: 'America/Winnipeg', label: 'Central Time (CT) — Winnipeg' },
  { value: 'America/Halifax', label: 'Atlantic Time (AT) — Halifax' },
  { value: 'America/St_Johns', label: 'Newfoundland (NT) — St. John\'s' },
  { value: 'America/Mexico_City', label: 'Central Time — Mexico City' },
  { value: 'America/Bogota', label: 'Colombia Time — Bogota' },
  { value: 'America/Sao_Paulo', label: 'Brasilia Time — Sao Paulo' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time — Buenos Aires' },
  { value: 'America/Santiago', label: 'Chile Time — Santiago' },
  { value: 'Europe/London', label: 'GMT/BST — London, Dublin' },
  { value: 'Europe/Paris', label: 'CET — Paris, Madrid, Rome' },
  { value: 'Europe/Berlin', label: 'CET — Berlin, Amsterdam' },
  { value: 'Europe/Helsinki', label: 'EET — Helsinki, Bucharest' },
  { value: 'Europe/Moscow', label: 'MSK — Moscow' },
  { value: 'Asia/Dubai', label: 'Gulf Time — Dubai' },
  { value: 'Asia/Kolkata', label: 'IST — India' },
  { value: 'Asia/Singapore', label: 'SGT — Singapore, Kuala Lumpur' },
  { value: 'Asia/Shanghai', label: 'CST — Beijing, Shanghai' },
  { value: 'Asia/Tokyo', label: 'JST — Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: 'AEST — Sydney, Melbourne' },
  { value: 'Pacific/Auckland', label: 'NZST — Auckland' },
];

/**
 * Returns the curated list, prepending the user's currently-selected zone if it
 * isn't already present (e.g. a browser-detected zone outside the short-list) so
 * the active value always renders a label.
 */
export function getTimezoneOptions(currentTz: string): TimezoneOption[] {
  const existing = TIMEZONE_LIST.find((tz) => tz.value === currentTz);
  if (existing) return TIMEZONE_LIST;
  return [
    { value: currentTz, label: `${currentTz.replace(/_/g, ' ')} (detected)` },
    ...TIMEZONE_LIST,
  ];
}
